import React, { useState } from 'react';
import axios from 'axios';
import PageHero from '../components/PageHero';
import { contactInfo } from '../data/mock';
import { Phone, Mail, Send, CheckCircle2, Building2, Clock, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', service: '', location: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const onPhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm({ ...form, phone: digits });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // Client-side validation: phone must be exactly 10 digits
    if (!/^\d{10}$/.test(form.phone)) {
      setStatus('error');
      setErrorMsg('Please enter a valid 10-digit mobile number.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const payload = { ...form };
      // remove empty optional fields to satisfy validation
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      await axios.post(`${API}/enquiries`, payload);
      setStatus('success');
      setForm({ name: '', company: '', email: '', phone: '', service: '', location: '', message: '' });
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err) {
      setStatus('error');
      const d = err?.response?.data?.detail;
      let msg = 'Something went wrong. Please try again or WhatsApp us.';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length) msg = d.map((e) => e?.msg || 'Invalid input').join(' · ');
      else if (d && typeof d === 'object' && d.msg) msg = String(d.msg);
      setErrorMsg(msg);
    }
  };

  return (
    <div>
      <PageHero
        title="Let&rsquo;s design a facility program for your business."
        subtitle="Share a few details below and our senior consultants will reach out within 24 hours."
        image="https://images.unsplash.com/photo-1501324682324-0d8d7c4b9706?auto=format&fit=crop&w=2000&q=80"
        breadcrumbs={[{ label: 'Contact' }]}
      />

      {/* Quick contact strip */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { Icon: Phone, title: 'Call us', primary: '0824-2438807', secondary: 'Mon–Sat, 9am–6pm IST' },
            { Icon: Mail, title: 'Enquiry / Proposal', primary: 'enquiry@durgafacility.com', secondary: 'info@durgafacility.com' },
            { Icon: Clock, title: 'Response time', primary: 'Within 24 hours', secondary: 'On-site audit in 72 hours' },
          ].map(({ Icon, title, primary, secondary }) => (
            <div key={title} className="flex items-start gap-4 p-5 rounded-2xl border border-slate-100 hover:border-slate-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-xl bg-slate-100 text-[#0A2540] flex items-center justify-center shrink-0"><Icon size={22} /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{title}</div>
                <div className="font-bold text-slate-900 mt-1">{primary}</div>
                <div className="text-xs text-slate-500 mt-0.5">{secondary}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Form + info */}
      <section className="py-24 bg-white">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12">
          {/* Form */}
          <form onSubmit={onSubmit} className="bg-slate-50 rounded-3xl p-8 md:p-10 border border-slate-100">
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-3">Request Proposal</div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-2">Tell us about your facility.</h2>
            <p className="text-sm text-slate-500 mb-8">Prefer email? Write to <a href="mailto:enquiry@durgafacility.com" className="text-[#0A2540] font-semibold hover:underline">enquiry@durgafacility.com</a></p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Full Name *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Company</label>
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Work Email *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Phone *</label>
                <input required type="tel" inputMode="numeric" maxLength={10} placeholder="10-digit mobile" title="Enter your 10-digit mobile number" value={form.phone} onChange={onPhoneChange} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" data-testid="contact-phone-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Service Interested In</label>
                <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all">
                  <option value="">Select a service</option>
                  <option>Integrated Facility Management</option>
                  <option>Pest Management Services</option>
                  <option>Front Office Management</option>
                  <option>On-demand Services</option>
                  <option>Security Services</option>
                  <option>Multiple / Not sure</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Location / City</label>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Tell us more</label>
              <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" placeholder="Facility type, area, headcount, current provider, expected start date..." />
            </div>
            <button type="submit" disabled={status === 'submitting'} className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 btn-cta font-semibold px-8 py-3.5 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed">
              <Send size={16} /> {status === 'submitting' ? 'Sending…' : 'Send Request'}
            </button>
            {status === 'success' && (
              <div className="mt-4 text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-4 py-3 flex items-center gap-2">
                <CheckCircle2 size={16} /> Thanks! Our team will reach out within 24 hours.
              </div>
            )}
            {status === 'error' && (
              <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3 flex items-center gap-2">
                <AlertCircle size={16} /> {errorMsg}
              </div>
            )}
          </form>

          {/* Offices */}
          <div className="space-y-6">
            {[contactInfo.registered, contactInfo.bangalore, contactInfo.mumbai].map((office, i) => {
              const hasMap = !!office.mapUrl;
              const openMap = () => window.open(office.mapUrl, '_blank', 'noopener,noreferrer');
              const onKeyDown = (e) => {
                if (hasMap && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  openMap();
                }
              };
              return (
                <div
                  id={i === 1 ? 'bangalore' : i === 2 ? 'mumbai' : undefined}
                  key={office.title}
                  onClick={hasMap ? openMap : undefined}
                  onKeyDown={onKeyDown}
                  role={hasMap ? 'link' : undefined}
                  tabIndex={hasMap ? 0 : undefined}
                  className={`bg-white p-6 rounded-2xl border border-slate-100 hover:shadow-lg transition-all ${hasMap ? 'cursor-pointer hover:border-[#F5B800]/40 group' : ''}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-[#0A2540]">
                      <Building2 size={16} />
                      <div className="text-xs font-bold uppercase tracking-widest">{office.title}</div>
                    </div>
                    {hasMap && (
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 group-hover:text-[#0A2540] transition-colors">
                        View on map →
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{office.address}</p>
                  <div className="space-y-2 text-sm">
                    {office.phone && (
                      <a
                        href={`tel:${office.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2.5 text-slate-800 hover:text-[#0A2540] transition-colors w-fit"
                      >
                        <Phone size={14} className="text-[#F5B800]" /> <span className="font-semibold">{office.phone}</span>
                      </a>
                    )}
                    {office.email && (
                      <a
                        href={`mailto:${office.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2.5 text-slate-800 hover:text-[#0A2540] transition-colors w-fit underline decoration-dotted decoration-slate-400 underline-offset-4"
                      >
                        <Mail size={14} className="text-[#F5B800]" /> {office.email}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24 bg-white">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
            <iframe
              title="Durga Mangalore Office"
              src="https://www.google.com/maps?q=Kulshekar,Mangalore&output=embed"
              className="w-full h-[400px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
